import clsx from "clsx";
import React, { createRef, useCallback, useEffect, useState } from "react";
import * as ReactDOM from "react-dom";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import Button from "../Button";
import Card from "../Card";
import styles from "./Toast.module.scss";

type TVerticalSide = "top" | "bottom";
type THorizontalSide = "left" | "right";
type TPosition =
  | TVerticalSide
  | THorizontalSide
  | `${TVerticalSide}-${THorizontalSide}`;

const MessageTypes = ["error", "success", "warning", "info"] as const;

export type TToastOptions = {
  position?: TPosition;
  type?: (typeof MessageTypes)[number];
  title?: string;
  message: string;
  timeout?: number;
};

type TCompleteToastOptions = TToastOptions & {
  id: number;
  ref: React.RefObject<HTMLDivElement>;
};

class Toast {
  static call(options: TToastOptions) {
    if (typeof window === "undefined") return;
    if (!options.timeout) options.timeout = 3000;
    document.dispatchEvent(
      new CustomEvent<TCompleteToastOptions>("toast", {
        detail: { ...options, id: Number(new Date()), ref: createRef() },
      }),
    );
  }
  static success(options: Omit<TToastOptions, "type">) {
    this.call({ ...options, type: "success" });
  }
  static info(options: Omit<TToastOptions, "type">) {
    this.call({ ...options, type: "info" });
  }
  static warning(options: Omit<TToastOptions, "type">) {
    this.call({ ...options, type: "warning" });
  }
  static error(options: Omit<TToastOptions, "type">) {
    this.call({ ...options, type: "error" });
  }
}

export type ToastProps = TToastOptions & {
  close: () => void;
};

export const ToastCard = React.forwardRef<HTMLDivElement, ToastProps>(
  (props: ToastProps, ref) => {
    const {
      message,
      title,
      type = "info",
      close,
      position = "bottom-right",
    } = props;
    const classes = clsx([
      styles["toast"],
      !title && styles[`toast--no-title`],
      styles[`toast--position-${position}`],
      styles[`toast--type-${type}`],
    ]);
    return (
      <Card
        ref={ref}
        borderRadius={"3"}
        className={classes}
        borderThickness={2}
      >
        {title && <span>{title}</span>}
        <p>{message}</p>
        <Button onClick={close} borderRadius="circle" width={35} height={35}>
          <svg
            style={{
              width: "24px",
              fill: "#fff",
            }}
            viewBox="0 0 24 24"
          >
            {" "}
            <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </Button>
      </Card>
    );
  },
);

export const ToastProvider = () => {
  const [bus, setBus] = useState<TCompleteToastOptions[]>([]);
  const [appContainer, setAppContainer] = useState<Element | null>(null);
  useEffect(() => {
    setAppContainer(
      document.querySelector("#root") || document.querySelector("#__next"),
    );
  }, []);
  const handleClose = (e: TCompleteToastOptions) => {
    setBus((old) => {
      const tmp = [...old];
      tmp.splice(
        tmp.findIndex((i) => e.id === i.id),
        1,
      );
      return tmp;
    });
  };
  const handler = useCallback((e: CustomEventInit<TCompleteToastOptions>) => {
    if (e.detail) {
      window.requestAnimationFrame(() => {
        setBus((old) => [...old, e.detail as TCompleteToastOptions]);
      });
      setTimeout(() => {
        handleClose(e.detail as TCompleteToastOptions);
      }, e.detail.timeout);
    }
  }, []);
  const getToastToRender = () => {
    const map = new Map<TPosition, TCompleteToastOptions[]>([
      ["bottom", []],
      ["bottom-left", []],
      ["bottom-right", []],
      ["left", []],
      ["right", []],
      ["top", []],
      ["top-left", []],
      ["top-right", []],
    ]);
    bus.forEach((toast) => {
      const { position = "bottom-right" } = toast;
      map.has(position) || map.set(position, []);
      map.get(position)?.push(toast);
    });
    return Array.from(map);
  };
  useEffect(() => {
    document.addEventListener("toast", handler);
    () => {
      document.removeEventListener("toast", handler);
    };
  }, []);
  return appContainer
    ? ReactDOM.createPortal(
        <div className={styles["provider"]}>
          {getToastToRender().map(([position, toasts]) => {
            return (
              <TransitionGroup
                className={styles[`provider__position-${position}`]}
                key={position}
              >
                {toasts.map((option) => (
                  <CSSTransition
                    key={position + option.id}
                    nodeRef={option.ref}
                    classNames={{
                      enter: "fgadfgadfg",
                      enterDone:
                        styles[`toast--position-${position}--enter-done`],
                      exit: styles[`toast--position-${position}--exit`],
                    }}
                    unmountOnExit
                    timeout={{ enter: 0, exit: 500 }}
                  >
                    <ToastCard
                      close={() => {
                        handleClose(option);
                      }}
                      {...option}
                    />
                  </CSSTransition>
                ))}
              </TransitionGroup>
            );
          })}
        </div>,
        appContainer,
      )
    : null;
};

export default Toast;
